import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useState } from "react";

import { fetcherAuth } from "utils/api";

interface Props {
  open: boolean;
  handleClose: () => void;
}

export default function EnableImport({ open, handleClose }: Props) {
  const { getToken } = useAuth();
  const { currentOrg, setCurrentOrg } = useContext(OrganizationUserContext);
  const {
    emailHealth,
    setOpenIntegrationDialog,
    setIntegrationDialogText,
    setIntegrationDialogTitle,
  } = useContext(UserIntegrationsContext);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [allowImportEmail, setAllowImportEmail] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const { setErrorAlert } = useContext(AlertContext);

  const toggleImportEmail = () => {
    setAllowImportEmail(!allowImportEmail);
    if (error) {
      setError(false);
    }
  };
  const confirmUpload = async () => {
    if (!allowImportEmail) {
      setError(true);
      return;
    }

    if (!emailHealth) {
      setIntegrationDialogText(
        "In order to enable import emails, we need read access to your Google account to automatically sync your emails ✨",
      );
      setIntegrationDialogTitle("Connect your Gmail");
      setOpenIntegrationDialog(true);
      return;
    }
    setConfirmLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/targeted-check-ins/generate-for-user`,
        "POST",
        {},
        {
          allowImportEmail,
        },
      );
      setCurrentOrg((prev) => {
        const today = Math.floor(new Date().getTime() / 1000);
        if (prev) {
          prev.allowImportEmail = allowImportEmail;
          prev.lastGeneratedCheckInsAt = today;
        }
        return prev;
      });
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle variant="h4" sx={{ textAlign: "center" }}>
        Enable Access
      </DialogTitle>
      <DialogContent>
        <Typography paragraph sx={{ textAlign: "center", fontSize: "16px" }}>
          🪄 Sync your brand emails to let Bento organize your partnerships,
          draft smart follow-ups, and help you stay on top of past
          collaborations.
        </Typography>
        <Typography>Please agree to the terms below:</Typography>
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={toggleImportEmail}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={allowImportEmail}
                  disableRipple
                  tabIndex={-1}
                  inputProps={{ "aria-labelledby": "allow-read-emails" }}
                />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={
                  error
                    ? {
                        color: "error",
                      }
                    : {}
                }
                id="allow-read-emails"
                primary="I agree to import my partnership emails and brand contacts into Bento *"
                secondary="This allows Bento to organize your conversations, set follow-ups, and recommend responses. 🔒 Your data stays private and is only visible to you."
              />
            </ListItemButton>
          </ListItem>
        </List>
        <Typography variant="caption">
          <Link
            sx={{ textDecoration: "none" }}
            href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento's%20Import%20Contacts%20Agreement-%20June%202025.pdf"
            target="_blank"
            rel="noreferrer"
          >
            View Full Terms of Service
          </Link>
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={confirmUpload}
          loading={confirmLoading}
          disableElevation
        >
          Continue
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
